import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, Plus, FileText, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_PILL = {
  Pending: "bg-amber-500 text-slate-900 border-amber-700",
  Approved: "bg-emerald-700 text-white border-emerald-900",
  Rejected: "bg-rose-700 text-white border-rose-900",
};

export default function ToolRequestsList() {
  const { user, isPrivileged } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | pending | mine
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (tab === "pending") params.status = "Pending";
        if (tab === "mine") params.mine_only = true;
        const { data } = await api.get("/tool-requests", { params });
        setRows(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [tab]);

  const counts = useMemo(() => ({
    total: rows.length,
    pending: rows.filter((r) => r.Status === "Pending").length,
  }), [rows]);

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto" data-testid="tool-requests-list">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1 flex items-center gap-1.5">
            <Wrench className="h-3 w-3" /> Tool Requests
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-slate-900 uppercase tracking-tight leading-none">
            Approval Letters
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Crew tool requests awaiting supervisor approval.
          </p>
        </div>
        <Link
          to="/tool-requests/new"
          data-testid="tool-requests-new-btn"
          className="rg-tap bg-emerald-700 hover:bg-emerald-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center gap-2 px-4 min-h-[48px] border-2 border-emerald-900"
        >
          <Plus className="h-4 w-4" />
          New request
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {["all", "pending", "mine"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`tool-requests-tab-${t}`}
            className={`rg-tap px-3 text-xs font-bold uppercase tracking-wider border-2 rounded-sm ${
              tab === t
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
            }`}
          >
            {t === "all" ? "All" : t === "pending" ? "Pending" : "My requests"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rg-card p-12 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rg-card p-12 text-center text-slate-500" data-testid="tool-requests-empty">
          <Wrench className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <div className="font-bold text-slate-700">No tool requests yet.</div>
          <Link to="/tool-requests/new" className="text-emerald-700 underline text-sm">
            Create the first one →
          </Link>
        </div>
      ) : (
        <div className="rg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-600">
              <tr>
                <th className="py-2.5 px-3 text-left">Request #</th>
                <th className="py-2.5 px-3 text-left">Project / Station</th>
                <th className="py-2.5 px-3 text-left">Requestor</th>
                <th className="py-2.5 px-3 text-left">Tools</th>
                <th className="py-2.5 px-3 text-left">Created</th>
                <th className="py-2.5 px-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav(`/tool-requests/${r.id}`)}
                  data-testid={`tool-requests-row-${r.Request_Number}`}
                >
                  <td className="py-2.5 px-3 font-mono font-bold text-slate-900">{r.Request_Number}</td>
                  <td className="py-2.5 px-3 text-slate-700">{r.Project_Station}</td>
                  <td className="py-2.5 px-3 text-slate-700">{r.Requestor_Name}</td>
                  <td className="py-2.5 px-3 text-slate-700">{(r.Tool_Snapshot || []).length}</td>
                  <td className="py-2.5 px-3 text-slate-500 text-xs">
                    {r.Created_At ? new Date(r.Created_At).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm ${STATUS_PILL[r.Status] || ""}`}>
                      {r.Status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
